import type { Vendor, VendorPlanResolution } from '@shopgnd/shared';
import {
  atQuotaLimit,
  getPlanDefinition,
  normalizePlanId,
  planHasFeature as sharedPlanHasFeature,
  quotaForPlan,
  quotaLabel,
  resolveVendorPlanId,
  type QuotaKind,
} from '@shopgnd/shared';
import type { PlanId } from '@/lib/vendorPlans';
import { getRuntimePlanDefinitions } from '@/lib/platformPlanConfig';

export type VendorPlanFeature =
  | 'cardPayments'
  | 'analytics'
  | 'promotions'
  | 'flashSales'
  | 'bundles'
  | 'classifieds'
  | 'property_listings'
  | 'storeBanner'
  | 'featuredSlots';

const FEATURE_MIN_PLAN: Record<VendorPlanFeature, PlanId> = {
  cardPayments: 'growth',
  analytics: 'growth',
  promotions: 'growth',
  flashSales: 'growth',
  bundles: 'growth',
  classifieds: 'starter',
  property_listings: 'growth',
  storeBanner: 'growth',
  featuredSlots: 'growth',
};

const PLAN_RANK: Record<PlanId, number> = {
  starter: 0,
  growth: 1,
  pro: 2,
};

function definitions() {
  return getRuntimePlanDefinitions();
}

export function planIdFromVendor(vendor: Pick<Vendor, 'subscriptionPlanId'> | null | undefined): PlanId {
  return normalizePlanId(vendor?.subscriptionPlanId);
}

export function planHasFeatureForResolution(
  resolution: VendorPlanResolution,
  feature: VendorPlanFeature,
): boolean {
  if (resolution.state !== 'resolved') return false;
  if (feature === 'property_listings' || feature === 'classifieds') {
    return sharedPlanHasFeature(resolution.planId, feature, definitions());
  }
  return sharedPlanHasFeature(resolution.planId, feature, definitions());
}

export function atQuotaLimitForResolution(
  resolution: VendorPlanResolution,
  kind: QuotaKind,
  currentCount: number,
): boolean {
  if (resolution.state !== 'resolved') return false;
  return atQuotaLimit(resolution.planId, kind, currentCount, definitions());
}

export function maxListingsForPlan(planId: PlanId): number | null {
  return quotaForPlan(planId, 'product', definitions());
}

export function planMeetsMinimum(current: PlanId, required: PlanId): boolean {
  return PLAN_RANK[current] >= PLAN_RANK[required];
}

export function vendorHasFeature(
  vendor: Pick<Vendor, 'subscriptionPlanId'> | null | undefined,
  feature: VendorPlanFeature,
  vendorLoaded = true,
): boolean {
  const resolution = resolveVendorPlanId(vendor?.subscriptionPlanId, vendorLoaded);
  return planHasFeatureForResolution(resolution, feature);
}

export function quotaFor(
  vendor: Pick<Vendor, 'subscriptionPlanId'> | null | undefined,
  kind: QuotaKind,
  vendorLoaded = true,
): number | null {
  const resolution = resolveVendorPlanId(vendor?.subscriptionPlanId, vendorLoaded);
  if (resolution.state !== 'resolved') return null;
  return quotaForPlan(resolution.planId, kind, definitions());
}

export function vendorListingLimit(
  vendor: Pick<Vendor, 'subscriptionPlanId'> | null | undefined,
  vendorLoaded = true,
): number | null {
  return quotaFor(vendor, 'product', vendorLoaded);
}

export function vendorAtListingLimit(
  vendor: Pick<Vendor, 'subscriptionPlanId'> | null | undefined,
  currentCount: number,
  vendorLoaded = true,
): boolean {
  const resolution = resolveVendorPlanId(vendor?.subscriptionPlanId, vendorLoaded);
  return atQuotaLimitForResolution(resolution, 'product', currentCount);
}

export function vendorAtQuotaLimit(
  vendor: Pick<Vendor, 'subscriptionPlanId'> | null | undefined,
  kind: QuotaKind,
  currentCount: number,
  vendorLoaded = true,
): boolean {
  const resolution = resolveVendorPlanId(vendor?.subscriptionPlanId, vendorLoaded);
  return atQuotaLimitForResolution(resolution, kind, currentCount);
}

export function listingLimitLabel(
  vendor: Pick<Vendor, 'subscriptionPlanId'> | null | undefined,
  vendorLoaded = true,
): string {
  return quotaLabel(vendorListingLimit(vendor, vendorLoaded), 'listings');
}

export function propertyLimitLabel(
  vendor: Pick<Vendor, 'subscriptionPlanId'> | null | undefined,
  vendorLoaded = true,
): string {
  return quotaLabel(quotaFor(vendor, 'property', vendorLoaded), 'properties');
}

export function planQuotaSummary(planId: PlanId, defs = definitions()): string {
  const plan = getPlanDefinition(planId, defs);
  return [
    quotaLabel(plan.maxProductListings, 'products'),
    quotaLabel(plan.maxClassifiedListings, 'classifieds'),
    quotaLabel(plan.maxPropertyListings, 'properties'),
  ].join(' · ');
}

export function lowestPlanNameWithFeature(
  feature: VendorPlanFeature,
  defs = definitions(),
): string {
  const order: PlanId[] = ['starter', 'growth', 'pro'];
  for (const id of order) {
    if (sharedPlanHasFeature(id, feature, defs)) {
      return getPlanDefinition(id, defs).name;
    }
  }
  return getPlanDefinition('growth', defs).name;
}

export function vendorCanAccessPayouts(
  vendor: Pick<Vendor, 'subscriptionPlanId' | 'cardPaymentApprovalStatus'> | null | undefined,
  vendorLoaded = true,
): boolean {
  if (!vendor) return false;
  return (
    vendorHasFeature(vendor, 'cardPayments', vendorLoaded) &&
    (vendor.cardPaymentApprovalStatus ?? 'none') === 'approved'
  );
}

export function paidPlanNamesForCardPayments(defs = definitions()): string {
  const names = (['starter', 'growth', 'pro'] as PlanId[])
    .filter((id) => sharedPlanHasFeature(id, 'cardPayments', defs))
    .map((id) => getPlanDefinition(id, defs).name);
  if (names.length === 0) return 'Growth or Pro';
  if (names.length === 1) return names[0];
  return `${names.slice(0, -1).join(', ')} or ${names[names.length - 1]}`;
}

export function featureGateMessage(
  feature: VendorPlanFeature,
  _requiredPlan: PlanId = FEATURE_MIN_PLAN[feature],
): string {
  const planName = lowestPlanNameWithFeature(feature, definitions());
  switch (feature) {
    case 'cardPayments':
      return `Card payments require the ${planName} plan or higher. Starter stores accept cash on delivery only.`;
    case 'classifieds':
      return `Classifieds are included on every plan. Upgrade for higher listing limits and more seller tools.`;
    case 'property_listings':
      return `Agency property listings require the ${planName} plan and above.`;
    case 'analytics':
      return `Sales analytics require the ${planName} plan or higher.`;
    case 'promotions':
      return `Promotions and coupon codes require the ${planName} plan or higher.`;
    case 'flashSales':
      return `Flash sales require the ${planName} plan or higher.`;
    case 'bundles':
      return `Bundle deals require the ${planName} plan or higher.`;
    default:
      return `This feature requires the ${planName} plan or higher.`;
  }
}

export { vendorAcceptsCardAtCheckout, vendorOffersCod } from '@/lib/vendorCardPayments';
